/**
 * Type definitions and constants for Drill Timer
 */

// Drill Actions (for Rescue Ready template)
export const DRILL_ACTIONS = {
  DIVER_EMERGENCY_CALL: 'diver_emergency_call',
  STAFF_ARRIVAL: 'staff_arrival',
  EXTRICATION: 'extrication',
  ASSESSMENT: 'assessment',
  COMPRESSIONS: 'compressions',
  WETSUIT: 'wetsuit',
  COMPRESSIONS_RERUN: 'compressions_rerun',
  AED: 'aed',
  SHOCK: 'shock',
  DRILL: 'drill',
  CALLED_911: '911_called',
}

// Drill States
export const DRILL_STATES = {
  STARTED: 'started',
  ACKNOWLEDGED: 'acknowledged',
  ARRIVED: 'arrived',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
  STOPPED: 'stopped',
  RESTARTED: 'restarted',
  APPLIED: 'applied',
  REMOVAL: 'removal',
  ABORTED: 'aborted',
}

// Responder Roles
export const RESPONDER_ROLES = {
  IN_WATER_RESCUER: 'in_water_rescuer',
  EXTRICATION: 'extrication',
  ASSESSMENT: 'assessment',
  WETSUIT_REMOVAL: 'wetsuit_removal',
  AED_OPERATOR: 'aed_operator',
  COMPRESSIONS: 'compressions',
  O2_PROVIDER: 'o2_provider',
  DPIC: 'dpic',
}

// Role Labels
export const ROLE_LABELS = {
  [RESPONDER_ROLES.IN_WATER_RESCUER]: 'In-Water Rescuer',
  [RESPONDER_ROLES.EXTRICATION]: 'Extrication',
  [RESPONDER_ROLES.ASSESSMENT]: 'Assessment',
  [RESPONDER_ROLES.WETSUIT_REMOVAL]: 'Wetsuit Removal',
  [RESPONDER_ROLES.AED_OPERATOR]: 'AED Operator',
  [RESPONDER_ROLES.COMPRESSIONS]: 'Compressions',
  [RESPONDER_ROLES.O2_PROVIDER]: 'O2 Provider',
  [RESPONDER_ROLES.DPIC]: 'DPIC',
}

// Rescue Ready pre-defined actions
export const RESCUE_READY_ACTIONS = [
  {
    definition: DRILL_ACTIONS.DIVER_EMERGENCY_CALL,
    name: 'Diver Emergency Call',
    icon: 'alert-circle',
    states: [DRILL_STATES.ACKNOWLEDGED],
  },
  {
    definition: DRILL_ACTIONS.STAFF_ARRIVAL,
    name: 'First Staff Arrival',
    icon: 'user-plus',
    states: [DRILL_STATES.ARRIVED],
  },
  {
    definition: DRILL_ACTIONS.EXTRICATION,
    name: 'Extrication',
    icon: 'arrow-up-circle',
    states: [DRILL_STATES.STARTED, DRILL_STATES.COMPLETED],
  },
  {
    definition: DRILL_ACTIONS.ASSESSMENT,
    name: 'Assessment',
    icon: 'activity',
    states: [DRILL_STATES.STARTED, DRILL_STATES.COMPLETED],
  },
  {
    definition: DRILL_ACTIONS.COMPRESSIONS,
    name: 'Compressions',
    icon: 'heart',
    states: [DRILL_STATES.STARTED],
  },
  {
    definition: DRILL_ACTIONS.WETSUIT,
    name: 'Wetsuit Removal',
    icon: 'scissors',
    states: [DRILL_STATES.REMOVAL],
    hint: 'Compressions Paused',
  },
  {
    definition: DRILL_ACTIONS.COMPRESSIONS_RERUN,
    name: 'Compressions Resumed',
    icon: 'heart',
    states: [DRILL_STATES.RESTARTED],
  },
  {
    definition: DRILL_ACTIONS.AED,
    name: 'AED',
    icon: 'zap',
    states: [DRILL_STATES.APPLIED],
  },
  {
    definition: DRILL_ACTIONS.SHOCK,
    name: 'AED Shock',
    icon: 'zap',
    states: [DRILL_STATES.APPLIED],
  },
  {
    definition: DRILL_ACTIONS.DRILL,
    name: 'EMS Arrived / Drill Complete',
    icon: 'check-circle',
    states: [DRILL_STATES.COMPLETED, DRILL_STATES.ABORTED],
  },
]

/**
 * @typedef {Object} DrillTimer
 * @property {string} id
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} location
 * @property {string} name
 * @property {boolean} is_rescue_ready
 * @property {boolean} is_sequential
 * @property {boolean} is_non_sequential
 * @property {Array<{action: string, icon?: string}>} sequential
 * @property {Array<{action: string, icon?: string}>} non_sequential
 * @property {string[]} roles
 * @property {string} drill_type
 */

/**
 * @typedef {Object} DrillTimerLog
 * @property {string} [id]
 * @property {string} name
 * @property {string} location
 * @property {string} drill_name
 * @property {string} location_of_drill
 * @property {string[]} roles
 * @property {boolean} is_rescue_ready
 * @property {string} start_time - ISO datetime
 * @property {string} end_time - ISO datetime
 * @property {DrillActor} proctor
 * @property {DrillActor[]} responders
 * @property {ActionRecord[]} records
 * @property {string} notes
 * @property {string} lessons_learned
 * @property {string} [video] - S3 URL
 */

/**
 * @typedef {Object} DrillActor
 * @property {string} user_id
 * @property {string} user_name
 * @property {string[]} roles
 * @property {string} [signature] - S3 URL or data URL
 */

/**
 * @typedef {Object} ActionRecord
 * @property {string} action
 * @property {string} state
 * @property {string} time - ISO datetime
 */
